/**
 * hyperliquidOrderService — Client-side Hyperliquid order placement.
 *
 * Uses EIP-712 typed-data signing (MetaMask) + msgpack action-hash encoding
 * to place and cancel orders on Hyperliquid's /exchange endpoint.
 *
 * Signing flow:
 *   1. Build action object (order params)
 *   2. Encode via msgpack → keccak256 → connectionId (bytes32)
 *   3. Sign phantom-agent EIP-712 message via MetaMask
 *   4. POST { action, nonce, signature } to https://api.hyperliquid.xyz/exchange
 */

import { keccak256, BrowserProvider, zeroPadValue } from 'ethers';
import { encode as msgpackEncode } from '@msgpack/msgpack';
import type { HLOrderRequest, HLCancelRequest, LiveOrderResult } from '@arc/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const HL_EXCHANGE = 'https://api.hyperliquid.xyz/exchange';
const HL_INFO     = 'https://api.hyperliquid.xyz/info';

// EIP-712 domain (Hyperliquid's fixed phantom domain)
const HL_DOMAIN = {
  chainId:          1337,
  name:             'Exchange',
  verifyingContract: '0x0000000000000000000000000000000000000000',
  version:          '1',
} as const;

// EIP-712 types for the phantom agent
const HL_AGENT_TYPES = {
  Agent: [
    { name: 'source',       type: 'string'  },
    { name: 'connectionId', type: 'bytes32' },
  ],
};

// ─── Meta cache (coin → asset index mapping) ───────────────────────────────────

interface HLMeta {
  universe: Array<{ name: string; szDecimals: number; maxLeverage: number }>;
}

let _metaCache: HLMeta | null = null;
let _metaFetchedAt = 0;

async function fetchMeta(): Promise<HLMeta> {
  const now = Date.now();
  if (_metaCache && now - _metaFetchedAt < 3_600_000) return _metaCache;

  const res = await fetch(HL_INFO, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ type: 'meta' }),
  });

  if (!res.ok) throw new Error(`HL meta HTTP ${res.status}`);
  const data = await res.json() as HLMeta;
  _metaCache    = data;
  _metaFetchedAt = now;
  return data;
}

export async function getAssetIndex(symbol: string): Promise<number> {
  const meta = await fetchMeta();
  const idx  = meta.universe.findIndex(u => u.name === symbol);
  if (idx === -1) throw new Error(`Unknown symbol: ${symbol}`);
  return idx;
}

export async function getHLMeta(): Promise<HLMeta> {
  return fetchMeta();
}

// ─── Action hash (msgpack + keccak256) ────────────────────────────────────────

function computeActionHash(action: unknown, nonce: number, vaultAddress?: string): string {
  // Encode action with msgpack
  const packed = msgpackEncode(action);

  // Nonce: big-endian uint64 (8 bytes)
  const nonceBuf = new Uint8Array(8);
  const view     = new DataView(nonceBuf.buffer);
  // BigInt for safe uint64 precision
  view.setBigUint64(0, BigInt(nonce), false);

  let data: Uint8Array;
  if (vaultAddress) {
    const vaultHex   = vaultAddress.replace(/^0x/, '');
    const vaultBytes = new Uint8Array(vaultHex.match(/.{1,2}/g)!.map(b => parseInt(b, 16)));
    data = new Uint8Array([...packed, ...nonceBuf, 0x01, ...vaultBytes]);
  } else {
    data = new Uint8Array([...packed, ...nonceBuf, 0x00]);
  }

  return keccak256(data);
}

// ─── Sign an L1 action ────────────────────────────────────────────────────────

async function signL1Action(
  action:       unknown,
  nonce:        number,
  vaultAddress?: string,
  isMainnet     = true,
): Promise<{ r: string; s: string; v: number }> {
  if (!window.ethereum) throw new Error('MetaMask not found');

  const provider = new BrowserProvider(window.ethereum);
  const signer   = await provider.getSigner();

  const hash  = computeActionHash(action, nonce, vaultAddress);
  // Ensure hash is exactly 32 bytes (bytes32 in EIP-712)
  const connectionId = zeroPadValue(hash, 32);

  const signature = await signer.signTypedData(
    HL_DOMAIN,
    HL_AGENT_TYPES,
    {
      source:       isMainnet ? 'a' : 'b',
      connectionId,
    },
  );

  // Decompose signature into r, s, v
  const r = signature.slice(0, 66);
  const s = '0x' + signature.slice(66, 130);
  const v = parseInt(signature.slice(130, 132), 16);

  return { r, s, v };
}

// ─── Place order ───────────────────────────────────────────────────────────────

export async function placeHLOrder(
  params: HLOrderRequest,
): Promise<LiveOrderResult> {
  try {
    const nonce = Date.now();

    const action = {
      type:   'order',
      orders: [
        {
          a: params.assetIndex,
          b: params.isBuy,
          p: params.price,
          s: params.size,
          r: params.reduceOnly,
          t: params.orderType === 'Ioc'
            ? { limit: { tif: 'Ioc' } }
            : params.orderType === 'Alo'
            ? { limit: { tif: 'Alo' } }
            : { limit: { tif: 'Gtc' } },
        },
      ],
      grouping: 'na',
    };

    const signature = await signL1Action(
      action,
      nonce,
      params.vaultAddress,
    );

    const body: Record<string, unknown> = {
      action,
      nonce,
      signature,
    };
    if (params.vaultAddress) {
      body['vaultAddress'] = params.vaultAddress;
    }

    const res = await fetch(HL_EXCHANGE, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });

    if (!res.ok) {
      return { success: false, error: `HTTP ${res.status}` };
    }

    const data = await res.json() as {
      status: string;
      response?: { type: string; data?: { statuses: Array<{ filled?: { oid: number }; resting?: { oid: number }; error?: string }> } };
    };

    if (data.status !== 'ok') {
      return { success: false, error: 'Order rejected by HL' };
    }

    const statuses = data.response?.data?.statuses ?? [];
    const first    = statuses[0];

    if (first?.error) {
      return { success: false, error: first.error };
    }

    const oid = first?.filled?.oid ?? first?.resting?.oid;
    return {
      success:    true,
      ...(oid !== undefined ? { hlOrderId: String(oid) } : {}),
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[HL] placeOrder error:', msg);
    return { success: false, error: msg };
  }
}

// ─── Cancel order ──────────────────────────────────────────────────────────────

export async function cancelHLOrder(
  params: HLCancelRequest,
): Promise<LiveOrderResult> {
  try {
    const nonce  = Date.now();
    const action = {
      type:    'cancel',
      cancels: [{ a: params.assetIndex, o: params.orderId }],
    };

    const signature = await signL1Action(action, nonce);

    const res = await fetch(HL_EXCHANGE, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ action, nonce, signature }),
    });

    if (!res.ok) {
      return { success: false, error: `HTTP ${res.status}` };
    }

    const data = await res.json() as { status: string };
    return { success: data.status === 'ok' };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, error: msg };
  }
}

// ─── Account equity ────────────────────────────────────────────────────────────

export async function getHLAccountEquity(address: string): Promise<{ equity: number; available: number }> {
  try {
    const res = await fetch(
      `/api/trading/hyperliquid/account?address=${encodeURIComponent(address)}`,
    );
    if (!res.ok) return { equity: 0, available: 0 };
    const json = await res.json() as { ok: boolean; data?: { equity: number; available: number } };
    return json.data ?? { equity: 0, available: 0 };
  } catch {
    return { equity: 0, available: 0 };
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Format a number for HL order (strips trailing zeros, max precision) */
export function hlFormatSize(n: number, decimals: number): string {
  return n.toFixed(decimals).replace(/\.?0+$/, '') || '0';
}

/** Format a price for HL order (5 significant figures) */
export function hlFormatPrice(price: number): string {
  if (price <= 0) return '0';
  // Use toPrecision for significant digits, then clean trailing zeros
  return parseFloat(price.toPrecision(5)).toString();
}
